import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ExternalLink, Loader2, Plus } from 'lucide-react'
import { Navigate } from 'react-router-dom'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/hooks/useAuth'
import { usePlatformRoleContext } from '@/hooks/usePlatformRoleContext'
import { formatDate } from '@/lib/utils'

interface BusinessRow {
  id: string
  name: string
  website_url: string | null
  country: string | null
  created_at: string
  updated_at: string
  business_memberships?: { count: number }[] | { count: number }
}

function getLinkedUserCount(bm: BusinessRow['business_memberships']): number {
  if (!bm) return 0
  if (Array.isArray(bm) && bm[0]?.count != null) return bm[0].count
  if (typeof bm === 'object' && 'count' in bm) return (bm as { count: number }).count
  return 0
}

export default function PortalBusinesses() {
  const { user } = useAuth()
  const { canSeeClients, isSiteAdmin, isLoading: roleLoading, isError: roleError } = usePlatformRoleContext()

  const { data: managedCount } = useQuery({
    queryKey: ['managed-business-count', user?.id],
    queryFn: async (): Promise<number> => {
      if (!user?.id) return 0
      const { count, error } = await supabase
        .from('business_memberships')
        .select('business_id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('status', 'active')
        .in('role', ['business_owner', 'business_manager'])
      if (error) throw error
      return count ?? 0
    },
    enabled: !!user?.id,
  })

  const canAccess = canSeeClients || (managedCount ?? 0) > 0

  const { data: businesses, isLoading: businessesLoading } = useQuery({
    queryKey: ['businesses'],
    queryFn: async (): Promise<BusinessRow[]> => {
      const { data, error } = await supabase
        .from('businesses')
        .select('id, name, website_url, country, created_at, updated_at, business_memberships(count)')
        .order('name', { ascending: true })
      if (error) throw error
      return (data ?? []) as BusinessRow[]
    },
    enabled: canAccess,
  })

  if (roleLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-white/70" />
      </div>
    )
  }

  if (roleError) {
    return (
      <div className="max-w-md mx-auto py-16 text-center">
        <p className="text-white/80">Unable to verify your access. Check the browser console for details.</p>
        <p className="text-white/50 text-sm mt-2">Common causes: missing table, RLS policy, or incorrect user_id in platform_user_roles.</p>
      </div>
    )
  }

  if (!canAccess) {
    return <Navigate to="/portal" replace />
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-white">
            {(managedCount ?? 0) === 1 ? 'Company' : 'Companies'}
          </h1>
          <p className="text-white/60 text-sm mt-1">
            {canSeeClients ? 'All companies in the platform.' : 'Companies you manage.'}
          </p>
        </div>
        {isSiteAdmin && (
          <Link
            to="/portal/businesses/new"
            className="inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-white text-accent hover:bg-white/90 shrink-0 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add company
          </Link>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="rounded-xl border border-white/15 bg-white/5 overflow-hidden backdrop-blur-sm"
      >
        {businessesLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-white/70" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/15">
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Name</th>
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Country</th>
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Linked users</th>
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Created</th>
                  <th className="px-4 py-3 text-right w-12" aria-label="Website link" />
                </tr>
              </thead>
              <tbody>
                {(businesses ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-white/50 text-sm">
                      No companies yet.
                    </td>
                  </tr>
                ) : (
                  (businesses ?? []).map((b) => (
                    <tr
                      key={b.id}
                      className="border-b border-white/10 last:border-0 hover:bg-white/5 transition-colors group"
                    >
                      <td className="px-4 py-3 font-medium text-white">
                        {isSiteAdmin ? (
                          <Link
                            to={`/portal/businesses/${b.id}`}
                            className="hover:text-white/90 hover:underline"
                          >
                            {b.name}
                          </Link>
                        ) : (
                          b.name
                        )}
                      </td>
                      <td className="px-4 py-3 text-white/80">{b.country ?? '—'}</td>
                      <td className="px-4 py-3 text-white/80">
                        {getLinkedUserCount(b.business_memberships)}
                      </td>
                      <td className="px-4 py-3 text-white/70 text-sm">{formatDate(b.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        {b.website_url ? (
                          <a
                            href={b.website_url.startsWith('http') ? b.website_url : `https://${b.website_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                            title="Open website"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  )
}
